import { call, put, takeEvery, takeLatest, select, debounce } from 'redux-saga/effects';
import { LOCATION_CHANGE, goBack, getLocation } from 'connected-react-router';
import { matchPath } from 'connected-react-router';
// (stopSubmit removed in Epic 13f.)
import { addSuccess } from '../../features/notifications/actions';

const defaultItemName = 'Item';

const setDefaultValuesToConfig = ({
  itemName = defaultItemName,
  getSuccessCreateMsg = () => `${itemName} created successfully`,
  getSuccessUpdateMsg = () => `${itemName} updated successfully`,
  successDeleteMsg = () => `${itemName} deleted successfully`,
  ...options
}) => ({ itemName, getSuccessCreateMsg, getSuccessUpdateMsg, successDeleteMsg, ...options });

/**
 * getCRUDSagas
 * @param {String} basePageUrl
 * @param {Object} api
 * @param {Object} actions
 * @param {Object} constants
 * @param {String} statePropertyName
 * @param {String} formName
 * @param {Object} options
 * @returns {function(): any[]}
 */
export default function getCRUDSagas(basePageUrl, api, actions, constants, statePropertyName, formName, options = {}) {
  const config = setDefaultValuesToConfig(options);

  function* fetchItems() {
    try {
      const featureState = yield select((s) => s[statePropertyName]);

      const { data } = yield call(api.index, featureState);

      yield put(actions.addItems(data));
    } catch (e) {
      console.error(e);

      yield put(actions.loadItemsError(e));
    }
  }

  function* fetchAfterSearch() {
    yield put(actions.applySearch()); // reset current pagination page
    yield put(actions.loadItemsRequest());
  }

  // The reducer half of every CRUD feature lives in
  // `commonActionHandlers` and clears `isPushing` / patches the
  // local `data` array on:
  //   - `ADD_CREATED_ITEM`           (createItem success)
  //   - `ITEM_UPDATED_SUCCESSFULLY`  (updateItem success)
  //   - `ITEM_DELETED_SUCCESSFULLY`  (deleteItem success)
  // The sagas below dispatch those actions on the happy path so the
  // form's "saving…" state lifts and the list reflects the change
  // without a hard refresh. QA bug 3.2 (2026-05-12) had `isPushing`
  // wedged at `true` after a successful Owner save because the
  // updateItem saga skipped the `actions.updateItem(data)` dispatch
  // — and as a side effect the list still showed the stale row
  // until the user refreshed. Same shape applies to create / delete.
  function* createItem({ payload }) {
    try {
      const { data } = yield call(api.create, payload);
      // `api.create` returns the raw JSON:API envelope —
      // `data.data` is the per-row resource that the reducer's
      // `addItemToData` handler expects to push onto state.data.
      if (data && data.data) yield put(actions.addCreatedItem(data.data));
      yield put(goBack());

      yield put(addSuccess(config.getSuccessCreateMsg(payload, data)));
    } catch (e) {
      yield put(actions.createItemError(e));
    }
  }

  function* updateItem({ payload }) {
    try {
      const { pathname } = yield select(getLocation);
      const { params: { id } } = matchPath(pathname, { path: `${basePageUrl}/:id/edit` });

      const { data } = yield call(api.update, id, payload);
      // Drive the reducer's `updateItem` handler so `isPushing`
      // clears and the row inside `state.data` is replaced with
      // the freshly-saved server response. Skipping this is the
      // root cause of QA bug 3.2's "UI stuck loading" / "save
      // only after refresh" symptoms.
      if (data && data.data) yield put(actions.updateItem(data.data));
      yield put(goBack());

      yield put(addSuccess(config.getSuccessUpdateMsg(payload, data)));
    } catch (e) {
      console.error(e);
      yield put(actions.updateItemError(e));
    }
  }

  function* deleteItem() {
    try {
      const pathname = yield select((s) => s.router.location.pathname);
      const { params: { id } } = matchPath(pathname, { path: `${basePageUrl}/:id/(v_delete|delete)` });

      yield call(api.delete, id);
      // Tell the reducer to drop the row out of `state.data` and
      // flip `isPushing` back to false. Without this the row
      // visibly stays in the list until the next refetch.
      yield put(actions.deleteItem(id));
      yield put(goBack());

      yield put(addSuccess(config.successDeleteMsg(id)));
    } catch (e) {
      console.error(e);

      yield put(actions.deleteItemError(e));
    }
  }

  function* fetchItemsPageInit({ payload: { isFirstRendering, location: { pathname } } }) {
    const didOpenPage = (
      matchPath(pathname, { exact: true, path: `${basePageUrl}` })
      // "|| isFirstRendering" for case when you open a form and reload the page
      // and we MUST show list items behind the form.
      // Any path under the page's own URL is that page — this used to
      // enumerate `(new|edit|delete)` and so missed the fourth overlay
      // the moment one was added (`…/documents/bulk`, CREW-1277).
      || (isFirstRendering && matchPath(pathname, { path: `${basePageUrl}/` }))
    );

    if (didOpenPage) yield put(actions.loadItemsRequest());
  }

  /**
  * getFeatureSagas (rename)
  * each saga file should return all feature sagas
  * @returns {ForkEffect[]}
  */
  function getFeatureSagas() {
    return [
      // takeLatest (not takeEvery): paging/sorting/filtering fast
      // must cancel the previous in-flight fetch, else an older
      // page's response can resolve last and overwrite the store —
      // the new page renders the previous page's rows (2026-06-30).
      takeLatest([
        constants.SORT_ITEMS,
        constants.APPLY_FILTER,
        constants.CHANGE_PAGE_NUMBER,
        constants.CHANGE_PAGE_ITEMS_COUNT,
        constants.LOAD_ITEMS_REQUEST,
      ], fetchItems),
      takeEvery(constants.DELETE_ITEM_REQUEST, deleteItem),
      takeEvery(constants.CREATE_ITEM_REQUEST, createItem),
      takeEvery(constants.UPDATE_ITEM_REQUEST, updateItem),
      debounce(700, constants.CHANGE_SEARCH, fetchAfterSearch),
      takeEvery(LOCATION_CHANGE, fetchItemsPageInit),
    ];
  }

  getFeatureSagas.fetchItems = fetchItems;
  getFeatureSagas.fetchAfterSearch = fetchAfterSearch;
  getFeatureSagas.createItem = createItem;
  getFeatureSagas.updateItem = updateItem;
  getFeatureSagas.deleteItem = deleteItem;
  getFeatureSagas.fetchItemsPageInit = fetchItemsPageInit;
  getFeatureSagas.getFeatureSagas = getFeatureSagas;

  return getFeatureSagas;
}
